import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  useTheme,
  Grid2,
  Checkbox,
  CircularProgress,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  SwapHoriz as SwapIcon,
  AddBoxOutlined as AddBoxOutlinedIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";

interface SwageazyInventoryRackTransferPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const INITIAL_GENERATE_RACK_STATE = {
  open: false,
  error: null as string | null,
  loading: false,
};

const SwageazyInventoryRackTransferPopup: React.FC<
  SwageazyInventoryRackTransferPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { swageazyInventoryStore, warehouseStore } = useStores();
  const { showError } = useNotification();
  const form = swageazyInventoryStore.rackTransferFormData ?? {};
  const isPrefillLocked = Boolean(form.prefill_box_id);
  const [generateRack, setGenerateRack] = useState(INITIAL_GENERATE_RACK_STATE);
  const updateGenerateRack = (
    patch: Partial<typeof INITIAL_GENERATE_RACK_STATE>,
  ) => {
    setGenerateRack((current) => ({ ...current, ...patch }));
  };

  const boxes = form.boxes || [];
  const selectedCount = boxes.filter((box) => box.selected).length;

  useEffect(() => {
    if (!open) {
      setGenerateRack(INITIAL_GENERATE_RACK_STATE);
      return;
    }
    warehouseStore.searchWarehouse().catch(() => {
      showError("Error", "Failed to load warehouses.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !isPrefillLocked || !form.warehouse_id) return;
    const match = warehouseStore.warehouses?.find(
      (item) => String(item?.id) === String(form.warehouse_id),
    );
    if (!match) return;
    const label = String(match.identifier || match.id);
    if (form.warehouse_name === label) return;
    swageazyInventoryStore.setRackTransferFormData({
      warehouse_name: label,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPrefillLocked, form.warehouse_id, warehouseStore.warehouses]);

  const rackOptions = (swageazyInventoryStore.warehouseRacks || []).map(
    (rack) => ({
      id: rack.id,
      value: rack.id,
    }),
  );
  const destOptions = rackOptions.filter(
    (rack) =>
      rack.id.toUpperCase() !==
      String(form.source_rack_id || "").toUpperCase(),
  );

  const loadWarehouse = (warehouseId?: string) => {
    if (!warehouseId) return;
    swageazyInventoryStore
      .loadRackTransferWarehouse(warehouseId)
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load racks.",
        );
      });
  };

  const handleGenerateRack = async () => {
    if (generateRack.loading) return;
    if (!form.warehouse_id) {
      updateGenerateRack({ error: "Select a warehouse first." });
      return;
    }
    updateGenerateRack({ error: null, loading: true });
    try {
      await swageazyInventoryStore.createRack();
      updateGenerateRack({ open: false });
    } catch (err: any) {
      updateGenerateRack({
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to generate rack.",
      });
    } finally {
      updateGenerateRack({ loading: false });
    }
  };

  const canSubmit =
    Boolean(form.warehouse_id) &&
    Boolean(form.source_rack_id) &&
    Boolean(form.dest_rack_id) &&
    form.source_rack_id?.toUpperCase() !== form.dest_rack_id?.toUpperCase() &&
    selectedCount > 0 &&
    !generateRack.loading &&
    !swageazyInventoryStore.isRackTransferLoading &&
    !swageazyInventoryStore.isRackTransferLookupLoading &&
    !swageazyInventoryStore.isLoading;

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    p: 2,
    backgroundColor: theme.palette.customColors?.grayBackGroundColor,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 2200 }}
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "92vh" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SwapIcon color="primary" />
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Rack Transfer
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.text.secondary}
            >
              {isPrefillLocked && form.prefill_box_id
                ? `Moving box ${form.prefill_box_id} from ${form.source_rack_id || "this rack"}. Stock stays in the same box.`
                : "Move boxes from one rack to another. Stock stays in the same box."}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close rack transfer">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
            Warehouse
          </CustomTypography>
          <AppSearchDropdownMulti
            title="Select Warehouse"
            local
            isMultiSelect={false}
            disabled={isPrefillLocked}
            backgroundColor={theme.palette.customColors.grayBackGroundColor}
            borderColor={theme.palette.customColors.greyBodyBackground}
            minWidth="100%"
            list={
              warehouseStore.warehouses?.map((item) => ({
                id: String(item?.id),
                value: String(item?.identifier || item?.id),
              })) || []
            }
            value={
              form.warehouse_id
                ? [
                    {
                      id: String(form.warehouse_id),
                      value: String(form.warehouse_name || form.warehouse_id),
                    },
                  ]
                : []
            }
            handleSelect={(selectedItem: FilterValue[]) => {
              if (isPrefillLocked) return;
              if (selectedItem?.length > 0) {
                swageazyInventoryStore.setRackTransferFormData({
                  warehouse_id: selectedItem[0].id,
                  warehouse_name: selectedItem[0].value,
                  source_rack_id: undefined,
                  dest_rack_id: undefined,
                  boxes: [],
                });
                loadWarehouse(selectedItem[0].id);
              } else {
                swageazyInventoryStore.setRackTransferFormData({
                  warehouse_id: undefined,
                  warehouse_name: undefined,
                  source_rack_id: undefined,
                  dest_rack_id: undefined,
                  boxes: [],
                });
                swageazyInventoryStore.clearRackTransferLookups();
              }
            }}
          />
        </Box>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={sectionSx}>
              <CustomTypography variant="heading6" fontWeight={700}>
                From
              </CustomTypography>
              <CustomTypography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.75 }}>
                Source rack
              </CustomTypography>
              <AppSearchDropdownMulti
                title="Select source rack"
                local
                isMultiSelect={false}
                disabled={!form.warehouse_id || isPrefillLocked}
                backgroundColor={theme.palette.common.white}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="100%"
                list={rackOptions}
                value={
                  form.source_rack_id
                    ? [{ id: form.source_rack_id, value: form.source_rack_id }]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (isPrefillLocked) return;
                  swageazyInventoryStore.selectRackTransferSource(
                    selectedItem?.[0]?.id,
                  );
                }}
              />
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Boxes on this rack
                  </CustomTypography>
                  {boxes.length > 0 && !isPrefillLocked ? (
                    <AppButton
                      variant="transparentPrimary"
                      label={
                        selectedCount === boxes.length
                          ? "Clear"
                          : "Select all"
                      }
                      onClick={() =>
                        swageazyInventoryStore.toggleAllRackTransferBoxes(
                          selectedCount !== boxes.length,
                        )
                      }
                    />
                  ) : null}
                </Box>
                {swageazyInventoryStore.isRackTransferLookupLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <CustomTypography
                      variant="body3"
                      color={theme.palette.text.secondary}
                    >
                      Loading boxes...
                    </CustomTypography>
                  </Box>
                ) : boxes.length === 0 ? (
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.text.secondary}
                    sx={{ mt: 1 }}
                  >
                    {form.source_rack_id
                      ? "No active boxes are on this rack."
                      : "Select a source rack to see its boxes."}
                  </CustomTypography>
                ) : (
                  boxes.map((box) => (
                    <Box
                      key={box.id}
                      sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={Boolean(box.selected)}
                        disabled={isPrefillLocked}
                        onChange={(event) =>
                          swageazyInventoryStore.toggleRackTransferBox(
                            box.id,
                            event.target.checked,
                          )
                        }
                      />
                      <CustomTypography variant="body2">{box.id}</CustomTypography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={sectionSx}>
              <CustomTypography variant="heading6" fontWeight={700}>
                To
              </CustomTypography>
              <CustomTypography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.75 }}>
                Destination rack
              </CustomTypography>
              <AppSearchDropdownMulti
                title="Select destination rack"
                local
                isMultiSelect={false}
                disabled={!form.warehouse_id}
                backgroundColor={theme.palette.common.white}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="100%"
                list={destOptions}
                value={
                  form.dest_rack_id
                    ? [{ id: form.dest_rack_id, value: form.dest_rack_id }]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  swageazyInventoryStore.setRackTransferFormData({
                    dest_rack_id: selectedItem?.[0]?.id,
                  });
                }}
              />
              <Box
                sx={{ mt: 2, cursor: "pointer" }}
                onClick={() =>
                  setGenerateRack((current) => ({
                    ...current,
                    open: !current.open,
                  }))
                }
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AddBoxOutlinedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <CustomTypography
                    variant="heading7"
                    fontWeight={600}
                    color={theme.palette.primary.main}
                  >
                    Generate new rack
                  </CustomTypography>
                  {generateRack.open ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </Box>
              </Box>
              <Collapse in={generateRack.open}>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.text.secondary}
                  sx={{ mt: 1, display: "block" }}
                >
                  Creates the next rack id in this warehouse and selects it as
                  the destination.
                </CustomTypography>
                <Box sx={{ mt: 1.5 }}>
                  <AppButton
                    variant="primary"
                    label={
                      generateRack.loading ? "Generating..." : "Generate rack"
                    }
                    disabled={!form.warehouse_id || generateRack.loading}
                    onClick={() => void handleGenerateRack()}
                  />
                </Box>
                {generateRack.error ? (
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.error.main}
                    sx={{ mt: 1, display: "block" }}
                  >
                    {generateRack.error}
                  </CustomTypography>
                ) : null}
              </Collapse>
            </Box>
          </Grid2>
        </Grid2>

        <Box sx={{ mt: 2.5 }}>
          <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
            Reason
          </CustomTypography>
          <CustomTextField
            width="100%"
            placeholder="e.g. Reorganize shelf"
            value={form.reason || ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              swageazyInventoryStore.setRackTransferFormData({
                reason: event.target.value,
              });
            }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AppButton variant="grayscale" label="Cancel" onClick={onClose} />
        <AppButton
          variant="primary"
          label={
            swageazyInventoryStore.isRackTransferLoading ||
            swageazyInventoryStore.isLoading
              ? "Moving..."
              : "Move boxes"
          }
          disabled={!canSubmit}
          onClick={onSave}
        />
        {swageazyInventoryStore.isRackTransferLoading ? (
          <CircularProgress size={18} />
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default observer(SwageazyInventoryRackTransferPopup);
